import { FileText, Save } from "lucide-react";

import type { PotenciaFactura } from "@/lib/api";

const num = (n: number, d = 2) =>
  new Intl.NumberFormat("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: d }).format(n);

/**
 * Resumen de la factura de una PARCIALIDAD de potencia (vista CFDI: cantidad
 * MAW × precio por MW-año). Compartido entre /facturacion (con botón Guardar)
 * y el detalle de /facturas (solo lectura, sin `onGuardar`).
 */
export function ResumenPotencia({ r, onGuardar, guardando }: {
  r: PotenciaFactura;
  onGuardar?: () => void;
  guardando?: boolean;
}) {
  const money = (n: number, d = 2) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: r.moneda === "USD" ? "USD" : "MXN",
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }).format(n);
  const pct = `${(r.iva_pct * 100).toFixed(0)}%`;
  const avance = r.pc_mw_ano ? (r.acumulado_mw / r.pc_mw_ano) * 100 : 0;

  return (
    <div className="space-y-4">
      {r.advertencias.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm space-y-1">
          {r.advertencias.map((a, i) => (
            <div key={i}>⚠ {a}</div>
          ))}
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50 flex flex-wrap items-center gap-2">
          <FileText className="w-4 h-4 text-slate-500" />
          <span className="font-medium text-slate-800">
            {r.folio} — {r.contraparte}
          </span>
          <span className="text-xs text-slate-500">
            Periodo {r.ym} · <b>Pago {r.n_pago} de {r.num_parcialidades}</b> · <b>{r.moneda}</b>
          </span>
        </div>

        {/* Concepto, igual que en el CFDI */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b">
                <th className="px-3 py-2 text-right">Cantidad</th>
                <th className="px-3 py-2">Unidad SAT</th>
                <th className="px-3 py-2">Clave Producto-Servicio</th>
                <th className="px-3 py-2">Concepto / Descripción</th>
                <th className="px-3 py-2 text-right">Valor unitario</th>
                <th className="px-3 py-2 text-right">Importe</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="px-3 py-2 text-right tabular-nums">{num(r.cantidad, 4)}</td>
                <td className="px-3 py-2">{r.clave_unidad} — Megawatt</td>
                <td className="px-3 py-2">{r.clave_prod_serv} — Servicios eléctricos</td>
                <td className="px-3 py-2">{r.descripcion}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.precio_unitario)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{money(r.importe)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Totales (con los cargos/créditos manuales guardados, si los hay) */}
        <div className="border-t px-4 py-3 flex flex-col items-end gap-1 text-sm">
          {(r.manuales ?? []).length > 0 && (
            <>
              <div className="flex gap-8">
                <span className="text-slate-500">Importe de la parcialidad</span>
                <span className="tabular-nums w-40 text-right">{money(r.importe)}</span>
              </div>
              {(r.manuales ?? []).map((l, i) => (
                <div key={i} className="flex gap-8">
                  <span className="text-slate-500">
                    {l.monto >= 0 ? "Cargo manual" : "Crédito manual"}: {l.concepto}
                  </span>
                  <span className={`tabular-nums w-40 text-right ${l.monto < 0 ? "text-emerald-700" : ""}`}>
                    {money(l.monto)}
                  </span>
                </div>
              ))}
            </>
          )}
          <div className="flex gap-8">
            <span className="text-slate-500">SUBTOTAL</span>
            <span className="tabular-nums w-40 text-right">
              {money(r.importe + (r.manuales ?? []).reduce((s, l) => s + l.monto, 0))}
            </span>
          </div>
          <div className="flex gap-8">
            <span className="text-slate-500">I.V.A. ({pct})</span>
            <span className="tabular-nums w-40 text-right">{money(r.iva)}</span>
          </div>
          <div className="flex gap-8 font-semibold text-emerald-800 border-t pt-1">
            <span>TOTAL</span>
            <span className="tabular-nums w-40 text-right">{money(r.total)}</span>
          </div>
        </div>

        <div className="px-4 py-2 border-t text-xs text-slate-500 flex flex-wrap gap-x-6 gap-y-1">
          <span>
            Cálculo: {r.pc_mw_ano} MW-año ÷ {r.num_parcialidades} parcialidades ={" "}
            {num(r.cantidad, 4)} MAW × {money(r.precio_unitario)} / MW-año
          </span>
          {r.tc_informativo != null && (
            <span>
              Tipo de cambio informativo: {num(r.tc_informativo, 4)}
              {r.tc_fecha ? ` (${r.tc_fecha})` : ""}
            </span>
          )}
          <span>
            Acumulado tras este pago: <b>{num(r.acumulado_mw, 4)}</b> de {r.pc_mw_ano} MW-año
            ({num(avance, 1)}%) · {money(r.acumulado_importe)}
          </span>
        </div>
      </div>

      {onGuardar && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onGuardar}
            disabled={guardando}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {guardando ? "Guardando…" : "Guardar factura"}
          </button>
          <span className="text-xs text-slate-400">
            Guarda la parcialidad como factura del mes (una por mes por contrato) y abre su
            detalle, donde están el PDF y Enviar a Flamma.
          </span>
        </div>
      )}
    </div>
  );
}
